import type { TFunction } from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';

import { CaseLevel1Code } from 'shared/constants/common-cases';
import TableFilterComponent, {
  type Option,
} from 'shared/components/TableFilterComponent';
import AttachIcon from 'shared/icons/Attach';
import formatDatetime from 'shared/utils/formatDatetime';
import safeParseHtml from 'shared/utils/safeParseHtml';

import type { CooperativeCommonCase } from '../../models/types';
import { Status } from './CooperativeCommonCasesGrid.styled';
import { filterCaseLevel1, filterStatus, filterYear } from './filtersFn';

const columnHelper = createColumnHelper<CooperativeCommonCase>();

type CreateColumnsOptions = {
  t: TFunction;
  caseLevel1Options: Option<CaseLevel1Code>[];
  statusOptions: Option<number>[];
  yearOptions: Option<number>[];
};

export const createColumns = (options: CreateColumnsOptions) => {
  const { caseLevel1Options, statusOptions, yearOptions, t } = options;

  return [
    columnHelper.accessor('Title', {
      header: t('#portal.incident.label.title'),
      meta: { sx: { width: '10%' } },
    }),
    columnHelper.accessor('Space.Name', {
      header: t('##attribute#incident#uds_spaceid'),
      meta: { sx: { width: '10%' } },
      sortingFn: 'alphanumeric',
    }),
    columnHelper.accessor('CaseLevel1.Label', {
      header: t('#portal.incident.grid.label.uds_requesttype'),
      filterFn: filterCaseLevel1,
      meta: {
        sx: { width: '12%' },
        filterComponent: ({ column, table }) => (
          <TableFilterComponent
            column={column}
            table={table}
            selectAllByDefault
            showSelectAllOption
            selectAllLabel={'Kaikki tyypit'}
            options={caseLevel1Options}
          />
        ),
      },
    }),
    columnHelper.accessor('Status.Label', {
      header: t('##attribute#incident#statuscode'),
      filterFn: filterStatus,
      cell: (info) => {
        const status = info.row.original.Status;
        if (!status) return null;

        return <Status status={status.Value}>{status.Label}</Status>;
      },
      meta: {
        sx: { width: '12%' },
        filterComponent: ({ column, table }) => (
          <TableFilterComponent
            column={column}
            table={table}
            selectAllByDefault
            showSelectAllOption
            selectAllLabel={'Kaikki vaiheet'}
            options={statusOptions}
          />
        ),
      },
    }),
    columnHelper.accessor('Description', {
      header: t('##attribute#incident#uds_portaldescription'),
      cell: (info) => {
        const value = info.getValue();

        return value && safeParseHtml(value);
      },
      meta: { sx: { width: '29%' } },
    }),
    columnHelper.accessor('HasFiles', {
      enableSorting: false,
      header: t('#portal.incident.grid.label.attachments'),
      meta: {
        align: 'center',
        sx: { width: '9%' },
      },
      cell: (info) =>
        info.getValue() && <AttachIcon sx={{ fontSize: '1.3rem' }} />,
    }),
    columnHelper.accessor('CreatedOn', {
      header: t('##attribute#incident#createdon'),
      cell: (info) => formatDatetime(info.getValue()),
      filterFn: filterYear,
      sortingFn: 'datetime',
      meta: {
        sx: { width: '9%' },
        filterComponent: ({ column, table }) => (
          <TableFilterComponent
            column={column}
            table={table}
            selectAllByDefault
            selectAllLabel={'Vuosi'}
            showSelectAllOption
            options={yearOptions}
          />
        ),
      },
    }),
    columnHelper.accessor('ModifiedOn', {
      header: t('##attribute#incident#modifiedon'),
      cell: (info) => formatDatetime(info.getValue()),
      sortingFn: 'datetime',
      meta: { sx: { width: '9%' } },
    }),
  ];
};
